import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Users, Home, TrendingUp, Award, CheckCircle, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

export default function ClevelandAgentsPage() {
  const benefits = [
    {
      icon: Users,
      title: 'Investor Client Pipeline',
      description: 'Connect with active investors looking for deals in Cleveland. Build your investor-focused business.'
    },
    {
      icon: TrendingUp,
      title: 'Multiple Revenue Streams',
      description: 'Earn commissions on purchases, sales, and property management referrals from investor clients.'
    },
    {
      icon: Award,
      title: 'Position as Expert',
      description: 'Establish yourself as the go-to investor-friendly agent in Cleveland\'s competitive market.'
    },
    {
      icon: Building2,
      title: 'Deal Flow Access',
      description: 'Get early access to off-market deals to bring to your investor clients before they hit the MLS.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Real estate agents"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-6">
              <MapPin className="h-5 w-5 text-[#5DADE2]" />
              <span className="text-[#5DADE2] font-medium">Cleveland, Ohio</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6">
              Cleveland Agents: Grow Your Investor Business
            </h1>
            
            <p className="text-xl text-slate-300 leading-relaxed mb-8 max-w-xl">
              Connect with Cleveland's active investor community. Get listed in our directory and start receiving qualified leads.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button asChild size="lg" className="bg-[#5DADE2] hover:bg-blue-600 text-white rounded-full px-8 text-lg h-14">
                <Link to="/signup?role=realtor">
                  Join the Directory
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full px-8 text-lg h-14">
                <Link to="/directory?category=Realtor&area=Cleveland">
                  See Cleveland Agents
                </Link>
              </Button>
            </div>

            <div className="flex items-center gap-6 pt-8 border-t border-white/20">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-slate-300">Free listing</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-slate-300">Direct investor leads</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-slate-300">Build reviews</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Join NextFlip as a Cleveland Agent?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Tap into Cleveland's growing investor market and build a sustainable business.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-[#5DADE2]/10 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="h-6 w-6 text-[#5DADE2]" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{benefit.title}</h3>
                <p className="text-slate-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investor Demand Section */}
      <section className="py-24 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block bg-amber-500/20 text-amber-400 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              Cleveland Market Opportunity
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Cleveland Investors Need You
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              The demand for investor-savvy agents in Cleveland far exceeds the supply. Here's your chance to stand out.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10 text-center">
              <p className="text-4xl font-bold text-[#5DADE2] mb-2">2,400+</p>
              <p className="text-white font-medium">Active Investors</p>
              <p className="text-sm text-slate-400 mt-1">searching for Cleveland properties monthly</p>
            </div>
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10 text-center">
              <p className="text-4xl font-bold text-[#5DADE2] mb-2">68%</p>
              <p className="text-white font-medium">Out-of-State Buyers</p>
              <p className="text-sm text-slate-400 mt-1">need local agents they can trust</p>
            </div>
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10 text-center">
              <p className="text-4xl font-bold text-[#5DADE2] mb-2">5.2x</p>
              <p className="text-white font-medium">Repeat Business Rate</p>
              <p className="text-sm text-slate-400 mt-1">investors buy multiple properties per year</p>
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 max-w-4xl mx-auto">
            <h3 className="text-xl font-semibold text-white mb-6 text-center">What Cleveland Investors Are Saying:</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <blockquote className="border-l-4 border-[#5DADE2] pl-4">
                <p className="text-slate-300 italic">"Finding an agent in Cleveland who understands rental numbers has been impossible. I need someone who can move fast."</p>
                <cite className="text-sm text-slate-500 mt-2 block">— California Investor</cite>
              </blockquote>
              <blockquote className="border-l-4 border-[#5DADE2] pl-4">
                <p className="text-slate-300 italic">"I'm buying 3-4 properties a year in Cleveland. I just need an agent who gets it. I'll give them all my deals."</p>
                <cite className="text-sm text-slate-500 mt-2 block">— New York Investor</cite>
              </blockquote>
            </div>
            <div className="text-center mt-8">
              <Button asChild className="bg-[#5DADE2] hover:bg-blue-600 text-white rounded-full px-8">
                <Link to="/signup?role=realtor">
                  Become Their Go-To Agent
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What Investors Look For */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-6">
                What Cleveland Investors Want in an Agent
              </h2>
              <div className="space-y-4">
                {[
                  'Understanding of investment metrics (cap rate, cash-on-cash, ROI)',
                  'Access to off-market and pocket listings',
                  'Quick response times and weekend availability',
                  'Network of contractors, lenders, and property managers',
                  'Experience with rental properties and multi-family',
                  'Knowledge of Cleveland\'s best investment neighborhoods'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#5DADE2] to-blue-600 rounded-3xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Your Profile Includes</h3>
              <ul className="space-y-3">
                {[
                  'Professional bio and photo',
                  'Service areas and specialties',
                  'Contact information',
                  'Client reviews and ratings',
                  'Direct messaging from investors'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                    {item}
                  </li>
                ))}
              </ul>
              <Button asChild className="w-full mt-6 bg-white text-[#5DADE2] hover:bg-blue-50 rounded-full">
                <Link to="/signup?role=realtor">Create Your Profile</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#5DADE2]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Grow Your Cleveland Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join the agents already connecting with investors on NextFlip
          </p>
          <Button asChild size="lg" className="bg-white text-[#5DADE2] hover:bg-blue-50 rounded-full px-8 text-lg h-14">
            <Link to="/signup?role=realtor">
              Get Listed Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
